import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  Flex,
  Text,
  IconButton,
  useDisclosure,
  FormControl,
  FormLabel,
  Input as ChakraInput,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  useToast,
  Button,
  Td,
  Badge,
  Select,
  Icon,
  Stack,
  SimpleGrid,
} from "@chakra-ui/react";
import {
  FaPlus,
  FaCalendarAlt,
  FaAward,
  FaRegStickyNote,
  FaArrowUp,
  FaArrowDown,
  FaCertificate,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Table from "../../components/common/Table";
import { Link } from "react-router-dom";
import { BASE_URL } from "../../Globalapi/Globalapi";

const Grade = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [grades, setGrades] = useState([]);
  const [newGrade, setNewGrade] = useState({
    grade: "",
    from_percentage: "",
    to_percentage: "",
    description: "",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchField, setSearchField] = useState("grade");
  const toast = useToast();
  const navigate = useNavigate();

  // Authentication check useEffect remains the same
  useEffect(() => {
    const jwt = sessionStorage.getItem("jwt");
    const userType = sessionStorage.getItem("user_type");

    if (!jwt || userType !== "1") {
      toast({
        title: "Access Denied",
        description: "You are not authorized to view this page.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      navigate("/login");
    }
  }, [toast, navigate]);

  // Fetch grades useEffect remains the same
  useEffect(() => {
    const fetchGrades = async () => {
      try {
        const response = await axios.get(
          `${BASE_URL}/admin/exam/list_all_grade_master.php`
        );
        if (response.data && Array.isArray(response.data.data)) {
          setGrades(response.data.data);
        } else {
          throw new Error("Unexpected data format");
        }
      } catch (error) {
        toast({
          title: "An error occurred.",
          description: "Unable to fetch grades.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    };

    fetchGrades();
  }, [toast]);

  // handleAddGrade remains the same
  const handleAddGrade = async () => {
    try {
      const response = await axios.post(
        `${BASE_URL}/admin/exam/create_grade_master.php`,
        {
          grade: newGrade.grade,
          from_percentage: newGrade.from_percentage,
          to_percentage: newGrade.to_percentage,
          description: newGrade.description,
          pass: sessionStorage.getItem("user_id"),
        }
      );

      if (response.data.success) {
        toast({
          title: "Grade created.",
          description: "The new grade has been created successfully.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        onClose();

        // Fetch updated grades
        const fetchGrades = async () => {
          try {
            const response = await axios.get(
              `${BASE_URL}/admin/exam/list_all_grade_master.php`
            );
            if (response.data && Array.isArray(response.data.data)) {
              setGrades(response.data.data);
            } else {
              throw new Error("Unexpected data format");
            }
          } catch (error) {
            toast({
              title: "An error occurred.",
              description: "Unable to fetch updated grades.",
              status: "error",
              duration: 5000,
              isClosable: true,
            });
          }
        };
        fetchGrades();
      } else {
        throw new Error("Failed to create grade");
      }
    } catch (error) {
      toast({
        title: "An error occurred.",
        description: "Unable to create the grade.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Updated pagination handler
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Render row remains the same
  const renderRow = (grade) => (
    <>
      <Td p={2} fontSize="sm">
        <Box textAlign="center">{grade.grade}</Box>
      </Td>
      <Td p={2} fontSize="sm">
        <Box textAlign="right">{grade.from_percentage} %</Box>
      </Td>
      <Td p={2} fontSize="sm">
        <Box textAlign="right">{grade.to_percentage} %</Box>
      </Td>
      <Td p={2} fontSize="sm">
        <Box textAlign="center">{grade.description}</Box>
      </Td>
      <Td py="12px" px="20px" textAlign="left">
        <Box textAlign="center">
          <Badge
            colorScheme={grade.status === "Active" ? "green" : "green"}
            borderRadius="full"
            px={4}
            py={1}
            fontSize="11px"
            boxShadow="md"
            position="relative"
          >
            {grade.status === "Active" ? "Active" : "Active"}
          </Badge>
        </Box>
      </Td>
    </>
  );

  // Updated rows per page handler
  const handleRowsPerPageChange = (e) => {
    const newRowsPerPage = Number(e.target.value);
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1); // Reset to first page when changing rows per page
  };

  // Updated filtering and pagination logic
  const filteredGrades = grades.filter((grade) =>
    searchTerm
      ? grade[searchField]
          ?.toString()
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
      : true
  );

  const totalPages = Math.ceil(filteredGrades.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const currentData = filteredGrades.slice(
    startIndex,
    startIndex + rowsPerPage
  );

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 5; i++) {
          pages.push(i);
        }
      } else if (currentPage >= totalPages - 2) {
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        for (let i = currentPage - 2; i <= currentPage + 2; i++) {
          pages.push(i);
        }
      }
    }
    return pages;
  };

  return (
    <Box borderRadius="md" mx="auto" bg="white">
      <div style={{ backgroundColor: "#FBFBFD", height: "100vh" }}>
        <div className="teacher-list-container">
          <div className="header">
            <h1 style={{ fontSize: "20px", margin: 0 }}>Grade List</h1>
            <div className="action-buttons">
              <Button
                className="add-teacher-button"
                leftIcon={<FaPlus />}
                colorScheme="teal"
                onClick={onOpen}
              >
                <Box display={{ base: "none", md: "block" }}>Add Grade</Box>
              </Button>
            </div>
          </div>
          <div className="breadcrumb">
            <Link
              to="/admin"
              style={{ textDecoration: "none", color: "inherit" }}
            >
              <span>Dashboard</span>
            </Link>{" "}
            / <span>Exam</span> / <span>Grade</span>
          </div>
        </div>

        <VStack p={6} align="stretch">
          <Flex
            direction="row"
            justify="space-between"
            align="center"
            p={3}
            className="sb"
          >
            <Box>
              <Flex align="center">
                <Text mr={2}>Rows Per Page</Text>
                <Select
                  width="100px"
                  value={rowsPerPage}
                  onChange={handleRowsPerPageChange}
                  borderColor="#E9EDF4"
                >
                  <option value={5}>5</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </Select>
              </Flex>
            </Box>
          </Flex>

          <Table
            headers={[
              "Grade Name",
              "Percentage From",
              "Percentage To",
              "Description",
              "Status",
            ]}
            data={currentData}
            renderRow={renderRow}
            
          />
          
          <Flex justify="end" borderTopWidth="1px" p={3} className="sb2">
            <Button
              onClick={() => handlePageChange(currentPage - 1)}
              isDisabled={currentPage === 1}
            >
              Prev
            </Button>
            <Stack direction="row" mx={2}>
              {getPageNumbers().map((pageNum) => (
                <Button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  backgroundColor={
                    currentPage === pageNum ? "teal" : "gray.100"
                  }
                  color={currentPage === pageNum ? "white" : "black"}
                >
                  {pageNum}
                </Button>
              ))}
            </Stack>
            <Button
              onClick={() => handlePageChange(currentPage + 1)}
              isDisabled={currentPage === totalPages}
            >
              Next
            </Button>
          </Flex>
        </VStack>
      </div>

      {/* Modal remains the same */}
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="lg"
        motionPreset="slideInBottom"
      >
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
        <ModalContent
          borderRadius="lg"
          boxShadow="xl"
          bg="linear-gradient(135deg, #e0f7f7, #f0ffff)"
          color="gray.700"
          p={6}
          maxW="700px"
          maxH="550px"
          overflowY="auto"
        >
          <ModalHeader
            fontSize="xl"
            fontWeight="bold"
            textAlign="center"
            color="teal.600"
          >
            <Icon as={FaPlus} boxSize={5} mr={2} />
            Create New Grade
          </ModalHeader>
          <ModalBody
            overflowY="auto"
            px={4}
            css={{
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                background: "rgba(255, 255, 255, 0.2)",
                borderRadius: "10px",
                backdropFilter: "blur(8px)",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "rgba(173, 216, 230, 0.6)",
                borderRadius: "10px",
                backdropFilter: "blur(8px)",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "rgba(173, 216, 230, 0.9)",
              },
            }}
          >
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <FormControl isRequired mb={3}>
                <FormLabel>
                  {" "}
                  <Icon as={FaCertificate} mr={1} color="teal.500" />
                  Grade Name
                </FormLabel>
                <ChakraInput
                  bg="white"
                  _hover={{ borderColor: "teal.300" }}
                  placeholder="Enter grade name"
                  value={newGrade.grade}
                  onChange={(e) =>
                    setNewGrade({ ...newGrade, grade: e.target.value })
                  }
                />
              </FormControl>
              <FormControl isRequired mb={3}>
                <FormLabel>
                  {" "}
                  <Icon as={FaArrowDown} mr={1} color="teal.500" />
                  Percentage From
                </FormLabel>
                <ChakraInput
                  type="number"
                  bg="white"
                  _hover={{ borderColor: "teal.300" }}
                  placeholder="Enter percentage from"
                  value={newGrade.from_percentage}
                  onChange={(e) =>
                    setNewGrade({
                      ...newGrade,
                      from_percentage: e.target.value,
                    })
                  }
                />
              </FormControl>
              <FormControl isRequired mb={3}>
                <FormLabel>
                  {" "}
                  <Icon as={FaArrowUp} mr={1} color="teal.500" />
                  Percentage To
                </FormLabel>
                <ChakraInput
                  type="number"
                  bg="white"
                  _hover={{ borderColor: "teal.300" }}
                  placeholder="Enter percentage to"
                  value={newGrade.to_percentage}
                  onChange={(e) =>
                    setNewGrade({ ...newGrade, to_percentage: e.target.value })
                  }
                />
              </FormControl>
              <FormControl mb={3}>
                <FormLabel>
                  {" "}
                  <Icon as={FaRegStickyNote} mr={1} color="teal.500" />
                  Description
                </FormLabel>
                <ChakraInput
                  bg="white"
                  _hover={{ borderColor: "teal.300" }}
                  placeholder="Enter grade description"
                  value={newGrade.description}
                  onChange={(e) =>
                    setNewGrade({ ...newGrade, description: e.target.value })
                  }
                />
              </FormControl>
            </SimpleGrid>
          </ModalBody>
          <ModalFooter>
            <Button
              bgGradient="linear(to-r, teal.400, teal.600)"
              color="white"
              _hover={{ bgGradient: "linear(to-r, teal.500, teal.700)" }}
              mr={3}
              onClick={handleAddGrade}
            >
              Save
            </Button>
            {/* <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button> */}
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Grade;
